import { computed, unref } from 'vue';
import { useQueryClient, useMutation } from '@tanstack/vue-query';

import { taskKeys } from '@/composables/queryKeys';
import { useQueryHelpers } from '@/composables';
import yardTaskService from '@satellite/services/yard-tasks-service';

import type {
  IQueryArgs,
  ITask,
  ITaskListResponse,
  IServiceParams,
  IServiceOptions
} from '@/types/apiTypes';
import type { UseQueryOptions } from '@tanstack/vue-query';
import type { MaybeRef } from 'vue';

const { useSmartQuery, useSmartMutation } = useQueryHelpers();

/**
 * Mutation for updating a task.
 */
export function useUpdateTaskMutation(options?: {
  onSuccess?: (
    data: object,
    variables: {
      taskId: string;
      params: IServiceParams;
      options?: IServiceOptions;
    },
    context: unknown,
    queryClient: ReturnType<typeof useQueryClient>
  ) => void;
  onError?: (
    error: unknown,
    variables: {
      taskId: string;
      params: IServiceParams;
      options?: IServiceOptions;
    },
    context: unknown,
    queryClient: ReturnType<typeof useQueryClient>
  ) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation<
    object,
    unknown,
    {
      taskId: string;
      params: IServiceParams;
      options?: IServiceOptions;
    }
  >({
    mutationFn: ({ taskId, params, options }) =>
      yardTaskService.updateYardTask(taskId, params, options),
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.taskId) });
      void queryClient.invalidateQueries({ queryKey: taskKeys.list() });
      options?.onSuccess?.(data, variables, context, queryClient);
    },
    onError: (error, variables, context) => {
      options?.onError?.(error, variables, context, queryClient);
    }
  });
}

/**
 * TODO: (welington) review the use of useSmart*
 */
export default function useTaskQueries() {
  const getTasksQuery = (queryArgs?: IQueryArgs) =>
    useSmartQuery<ITaskListResponse>(
      taskKeys.list(),
      ({ params, options, signal }) => {
        const opts = options || {};
        return yardTaskService.getYardTaskList({
          query: {
            fields: opts.fields,
            joins: opts.joins
          },
          getResponseConfig: {
            includeMetaData: opts.includeMetaData
          },
          requestConfig: {
            params,
            signal
          }
        }) as Promise<ITaskListResponse>;
      },
      queryArgs
    );

  const getTaskQuery = (
    taskId: MaybeRef<string>,
    queryArgs?: {
      params?: MaybeRef<IServiceParams>;
      options?: MaybeRef<IServiceOptions>;
      enabled?: MaybeRef<boolean>;
    } & Omit<UseQueryOptions<ITask>, 'queryKey' | 'queryFn' | 'enabled'>
  ) => {
    const unwrappedTaskId = computed(() => unref(taskId));
    return useSmartQuery<ITask>(
      computed(() => taskKeys.detail(unwrappedTaskId.value)),
      ({ options, signal }) =>
        yardTaskService.getYardTask(unwrappedTaskId.value, {
          query: options,
          requestConfig: { signal }
        }),
      queryArgs
    );
  };

  const useCreateTaskMutation = () =>
    useSmartMutation(
      ({ params, options }: { params: IServiceParams; options?: IServiceOptions }) =>
        yardTaskService.createYardTask(params, options),
      {
        onSuccess: async (_data, _variables, _context, queryClient) => {
          await queryClient.invalidateQueries({ queryKey: taskKeys.list() });
        }
      }
    );

  const useUpdateTaskMutation = (options?: {
    onSuccess?: (
      data: object,
      variables: {
        taskId: string;
        params: IServiceParams;
        options?: IServiceOptions;
      },
      context: unknown,
      queryClient: ReturnType<typeof useQueryClient>
    ) => void;
    onError?: (
      error: unknown,
      variables: {
        taskId: string;
        params: IServiceParams;
        options?: IServiceOptions;
      },
      context: unknown,
      queryClient: ReturnType<typeof useQueryClient>
    ) => void;
  }) =>
    useSmartMutation(
      ({
        taskId,
        params,
        options
      }: {
        taskId: string;
        params: IServiceParams;
        options?: IServiceOptions;
      }) => yardTaskService.updateYardTask(taskId, params, options),
      {
        onSuccess: async (data, variables, context, queryClient) => {
          await queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.taskId) });
          await queryClient.invalidateQueries({ queryKey: taskKeys.list() });

          options?.onSuccess?.(data, variables, context, queryClient);
        },
        onError: (error, variables, context, queryClient) => {
          options?.onError?.(error, variables, context, queryClient);
        }
      }
    );

  return { getTasksQuery, getTaskQuery, useUpdateTaskMutation, useCreateTaskMutation };
}
